"use client";

import { motion, useReducedMotion } from "framer-motion";
import {
  Check,
  Code2,
  Gavel,
  LineChart,
  Microscope,
  Stethoscope,
} from "lucide-react";
import Image from "next/image";
import { type ComponentType, useEffect, useState } from "react";

type IconType = ComponentType<{ size?: number; className?: string }>;

interface Pairing {
  photo: string;
  expertName: string;
  expertTitle: string;
  flag: string;
  flagAlt: string;
  ExpertIcon: IconType;
  expertRate: number;
  tierColor: string;
  searchRole: string;
  searchCategory: string;
  SearchIcon: IconType;
  searchRate: string;
  searchLocation: string;
  searchCode: string;
  commission: number;
}

// Each pairing connects one of the expert personas (reusing the hero-badge
// photos) to an open search they fit, with the recruiter's commission on the
// match. Commission is ~20% of the expert rate, rounded.
const PAIRINGS: Pairing[] = [
  {
    photo: "/marketing/experts/expert-9.png",
    expertName: "J. Miller",
    expertTitle: "Sr. Software Engineer",
    flag: "🇺🇸",
    flagAlt: "US",
    ExpertIcon: Code2,
    expertRate: 145,
    tierColor: "var(--color-marketing-blue)",
    searchRole: "Backend Engineer",
    searchCategory: "Software Engineering",
    SearchIcon: Code2,
    searchRate: "$120-160/hr",
    searchLocation: "Remote · US",
    searchCode: "SR-4471",
    commission: 29,
  },
  {
    photo: "/marketing/experts/expert-2.png",
    expertName: "P. Patel",
    expertTitle: "Senior Data Scientist",
    flag: "🇮🇳",
    flagAlt: "IN",
    ExpertIcon: Microscope,
    expertRate: 165,
    tierColor: "var(--color-marketing-blue)",
    searchRole: "ML Data Scientist",
    searchCategory: "AI / ML",
    SearchIcon: LineChart,
    searchRate: "$140-180/hr",
    searchLocation: "Remote",
    searchCode: "SR-3920",
    commission: 33,
  },
  {
    photo: "/marketing/experts/expert-3.png",
    expertName: "O. Brown",
    expertTitle: "Corporate Counsel",
    flag: "🇬🇧",
    flagAlt: "UK",
    ExpertIcon: Gavel,
    expertRate: 240,
    tierColor: "var(--color-marketing-accent-purple)",
    searchRole: "Legal Reviewer",
    searchCategory: "Legal",
    SearchIcon: Gavel,
    searchRate: "$200-260/hr",
    searchLocation: "Remote · UK",
    searchCode: "SR-5108",
    commission: 48,
  },
  {
    photo: "/marketing/experts/expert-4.png",
    expertName: "M. Bauer",
    expertTitle: "Strategy Consultant",
    flag: "🇩🇪",
    flagAlt: "DE",
    ExpertIcon: LineChart,
    expertRate: 210,
    tierColor: "var(--color-marketing-accent-purple)",
    searchRole: "PE Diligence Expert",
    searchCategory: "Private Equity",
    SearchIcon: LineChart,
    searchRate: "$180-240/hr",
    searchLocation: "Remote · EU",
    searchCode: "SR-2287",
    commission: 42,
  },
  {
    photo: "/marketing/experts/expert-5.png",
    expertName: "Dr. C. Wei",
    expertTitle: "Cardiologist",
    flag: "🇨🇦",
    flagAlt: "CA",
    ExpertIcon: Stethoscope,
    expertRate: 285,
    tierColor: "var(--color-marketing-accent-green-dark)",
    searchRole: "Clinical Evaluator",
    searchCategory: "Clinical",
    SearchIcon: Stethoscope,
    searchRate: "$240-320/hr",
    searchLocation: "Remote · CA",
    searchCode: "SR-6610",
    commission: 57,
  },
];

const CYCLE_MS = 5200;
const SCRAMBLE_MS = 800;
const COUNTUP_MS = 700;
const SCRAMBLE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ#$@*&%";
const HEX_CHARS = "0123456789ABCDEF";

function useScramble(
  target: string,
  trigger: number,
  enabled: boolean,
  charset: string,
): string {
  const [display, setDisplay] = useState(target);
  useEffect(() => {
    if (!enabled) {
      setDisplay(target);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / SCRAMBLE_MS);
      const out = target
        .split("")
        .map((c, i) => {
          if (c === " " || c === "." || c === "-") {
            return c;
          }
          const lockAt = (i / Math.max(target.length, 1)) * 0.65 + 0.3;
          if (t >= lockAt) {
            return c;
          }
          return charset[Math.floor(Math.random() * charset.length)]!;
        })
        .join("");
      setDisplay(out);
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setDisplay(target);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- trigger is the intentional re-run signal
  }, [trigger, enabled]);
  return display;
}

function useCountUp(target: number, trigger: number, enabled: boolean): number {
  const [value, setValue] = useState(target);
  useEffect(() => {
    if (!enabled) {
      setValue(target);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / COUNTUP_MS);
      const eased = 1 - (1 - t) ** 3;
      setValue(Math.floor(target * eased));
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, enabled]);
  return value;
}

export default function RecruitersHeroMatch() {
  const reduced = useReducedMotion() ?? false;
  const animated = !reduced;
  const [idx, setIdx] = useState(0);
  const [glazing, setGlazing] = useState(false);

  useEffect(() => {
    if (!animated) {
      return;
    }
    const interval = setInterval(() => {
      setGlazing(true);
      const t1 = setTimeout(() => {
        setIdx((i) => (i + 1) % PAIRINGS.length);
        const t2 = setTimeout(() => {
          setGlazing(false);
        }, 130);
        return () => {
          clearTimeout(t2);
        };
      }, 280);
      return () => {
        clearTimeout(t1);
      };
    }, CYCLE_MS);
    return () => {
      clearInterval(interval);
    };
  }, [animated]);

  const p = PAIRINGS[idx]!;
  const ExpertIcon = p.ExpertIcon;
  const SearchIcon = p.SearchIcon;
  const expertName = useScramble(p.expertName, idx, animated, SCRAMBLE_CHARS);
  const searchCode = useScramble(p.searchCode, idx, animated, HEX_CHARS);
  const commission = useCountUp(p.commission, idx, animated);
  const bodyClass = glazing ? "rm-glaze" : "rm-clear";

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none relative w-full max-w-[560px] select-none"
    >
      <style>{`
        @keyframes rm-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes rm-shimmer { 0%{transform:translateX(-130%) skewX(-22deg)} 100%{transform:translateX(260%) skewX(-22deg)} }
        @keyframes rm-livedot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.35;transform:scale(.7)} }
        .rm-float{ animation: rm-float 6s ease-in-out infinite; }
        .rm-shimmer{ animation: rm-shimmer 6.5s ease-in-out infinite; animation-delay:1.2s; }
        .rm-livedot{ animation: rm-livedot 1.6s ease-in-out infinite; }
        .rm-glaze{ filter: blur(2px) saturate(.7) brightness(1.05); transition: filter 200ms ease-in; }
        .rm-clear{ filter: blur(0) saturate(1) brightness(1); transition: filter 350ms ease-out; }
        @media (prefers-reduced-motion: reduce){
          .rm-float,.rm-shimmer,.rm-livedot{ animation: none !important; }
        }
      `}</style>

      <div
        className={`flex flex-col items-center justify-center gap-0 sm:flex-row sm:items-center ${
          animated ? "rm-float" : ""
        }`}
      >
        {/* Expert card */}
        <motion.div
          initial={
            animated
              ? { opacity: 0, transform: "translateX(-28px) rotate(-3deg)" }
              : false
          }
          animate={{ opacity: 1, transform: "translateX(0px) rotate(0deg)" }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="border-marketing-border bg-marketing-cream relative z-10 w-[176px] overflow-hidden rounded-[14px] border"
          style={{
            boxShadow:
              "inset 1.5px -1.5px 3px rgba(96, 96, 96, 0.22), 4px 9px 20px rgba(0, 6, 183, 0.28)",
          }}
        >
          <div className="bg-marketing-blue flex items-center justify-between px-3 py-1.5">
            <span className="text-marketing-cream font-mono text-[9px] tracking-[0.22em] uppercase">
              Expert
            </span>
            <div className="flex items-center gap-1">
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: p.tierColor }}
              />
              <span className="text-marketing-cream/90 font-mono text-[8.5px] tracking-wider uppercase">
                Verified
              </span>
            </div>
          </div>

          <div className={bodyClass}>
            <div className="flex flex-col items-center px-3 pt-3 pb-2">
              <div
                key={`photo-${idx}`}
                className="border-marketing-border bg-marketing-cream-light relative h-[60px] w-[60px] rounded-full border-2"
              >
                <div className="absolute inset-0 overflow-hidden rounded-full">
                  <Image
                    src={p.photo}
                    alt=""
                    fill
                    sizes="60px"
                    className="object-cover"
                    draggable={false}
                  />
                </div>
                <div className="bg-marketing-blue border-marketing-cream absolute -right-1 -bottom-1 flex h-[24px] w-[24px] items-center justify-center rounded-full border-[3px]">
                  <ExpertIcon size={12} className="text-marketing-cream" />
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center gap-0.5 px-3 pb-1">
              <div className="flex h-[14px] items-center gap-1.5">
                <span className="text-foreground font-mono text-[11px] leading-none font-semibold tracking-[-0.02em]">
                  {expertName}
                </span>
                <span
                  className="text-[12px] leading-none"
                  aria-label={p.flagAlt}
                >
                  {p.flag}
                </span>
              </div>
              <span className="text-marketing-text-muted/60 font-mono text-[8.5px] tracking-[0.05em] uppercase">
                {p.expertTitle}
              </span>
            </div>

            <div className="border-marketing-border-light mx-3 mt-1.5 mb-2 flex items-center justify-between rounded-[6px] border bg-white/40 px-2.5 py-1.5">
              <span className="text-marketing-text-secondary font-mono text-[7px] tracking-[0.15em] uppercase">
                Rate
              </span>
              <span className="text-foreground font-mono text-[11px] leading-none font-semibold tabular-nums">
                ${p.expertRate}/hr
              </span>
            </div>

            <div className="flex items-center justify-center gap-1 px-3 pb-2.5">
              {["ID", "LI", "EM", "IP"].map((label) => (
                <div
                  key={label}
                  className="border-marketing-border bg-marketing-cream-light flex h-[15px] items-center justify-center gap-0.5 rounded-full border px-1.5"
                >
                  <Check
                    size={6}
                    className="text-marketing-accent-green-dark"
                    strokeWidth={3}
                  />
                  <span className="text-marketing-text-secondary font-mono text-[6.5px] tracking-wider">
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {animated && (
              <div
                className="rm-shimmer absolute inset-y-0 w-[40%]"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.28) 50%, transparent 100%)",
                }}
              />
            )}
          </div>
        </motion.div>

        {/* Connector */}
        <div className="relative flex shrink-0 items-center justify-center py-1 sm:w-[78px] sm:py-0">
          {/* Mobile: vertical draw */}
          <motion.div
            key={`vline-${idx}`}
            initial={animated ? { transform: "scaleY(0)" } : false}
            animate={{ transform: "scaleY(1)" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformOrigin: "top" }}
            className="block h-9 w-[2px] bg-white/55 sm:hidden"
          />
          {/* Desktop: horizontal draw */}
          <motion.div
            key={`hline-${idx}`}
            initial={animated ? { transform: "scaleX(0)" } : false}
            animate={{ transform: "scaleX(1)" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformOrigin: "left" }}
            className="hidden h-[2px] w-full bg-white/55 sm:block"
          />

          {/* Match chip */}
          <motion.div
            key={`chip-${idx}`}
            initial={
              animated
                ? { transform: "rotate(-8deg) scale(0.4)", opacity: 0 }
                : false
            }
            animate={{ transform: "rotate(0deg) scale(1)", opacity: 1 }}
            transition={{
              duration: 0.5,
              delay: animated ? 0.32 : 0,
              ease: [0.34, 1.56, 0.64, 1],
            }}
            className="border-marketing-border bg-marketing-cream absolute z-20 flex flex-col items-center rounded-full border px-2.5 py-1.5 whitespace-nowrap"
            style={{
              boxShadow: "0 4px 12px rgba(0, 6, 183, 0.22)",
            }}
          >
            <div className="flex items-center gap-1">
              <span className="bg-marketing-accent-green-dark flex h-[13px] w-[13px] items-center justify-center rounded-full">
                <Check size={8} className="text-white" strokeWidth={3.5} />
              </span>
              <span className="text-foreground font-mono text-[8.5px] font-semibold tracking-[0.12em] uppercase">
                Matched
              </span>
            </div>
            <span className="text-marketing-accent-green-text font-mono text-[10px] leading-none font-bold tabular-nums">
              +${commission}
            </span>
          </motion.div>
        </div>

        {/* Open search card */}
        <motion.div
          initial={
            animated
              ? { opacity: 0, transform: "translateX(28px) rotate(3deg)" }
              : false
          }
          animate={{ opacity: 1, transform: "translateX(0px) rotate(0deg)" }}
          transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="border-marketing-border bg-marketing-cream relative z-10 w-[176px] overflow-hidden rounded-[14px] border"
          style={{
            boxShadow:
              "inset 1.5px -1.5px 3px rgba(96, 96, 96, 0.22), 4px 9px 20px rgba(0, 6, 183, 0.28)",
          }}
        >
          <div className="bg-marketing-blue flex items-center justify-between px-3 py-1.5">
            <span className="text-marketing-cream font-mono text-[9px] tracking-[0.22em] uppercase">
              Open Search
            </span>
            <div className="flex items-center gap-1">
              <span
                className={`bg-marketing-accent-green-dark h-1.5 w-1.5 rounded-full ${
                  animated ? "rm-livedot" : ""
                }`}
              />
              <span className="text-marketing-cream/90 font-mono text-[8.5px] tracking-wider uppercase">
                Live
              </span>
            </div>
          </div>

          <div className={bodyClass}>
            <div className="flex flex-col items-center px-3 pt-3 pb-2">
              <div className="border-marketing-border bg-marketing-cream-light relative flex h-[60px] w-[60px] items-center justify-center rounded-[14px] border-2">
                <SearchIcon size={26} className="text-marketing-blue" />
                <div className="bg-marketing-blue border-marketing-cream absolute -right-1 -bottom-1 flex h-[24px] w-[24px] items-center justify-center rounded-full border-[3px]">
                  <span className="text-marketing-cream font-mono text-[10px] leading-none font-bold">
                    ?
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-center gap-0.5 px-3 pb-1">
              <span className="text-foreground text-[12px] leading-none font-semibold tracking-[-0.02em]">
                {p.searchRole}
              </span>
              <span className="text-marketing-text-muted/60 font-mono text-[8.5px] tracking-[0.05em] uppercase">
                {p.searchCategory}
              </span>
            </div>

            <div className="border-marketing-border-light mx-3 mt-1.5 mb-2 flex items-center justify-between rounded-[6px] border bg-white/40 px-2.5 py-1.5">
              <span className="text-marketing-text-secondary font-mono text-[7px] tracking-[0.15em] uppercase">
                Pays
              </span>
              <span className="text-foreground font-mono text-[10px] leading-none font-semibold tabular-nums">
                {p.searchRate}
              </span>
            </div>

            <div className="flex items-center justify-between px-3 pb-2.5">
              <span className="text-marketing-text-secondary font-mono text-[7.5px] tracking-[0.08em] uppercase">
                {p.searchLocation}
              </span>
              <span className="text-marketing-text-secondary font-mono text-[7.5px] tracking-[0.12em]">
                {searchCode}
              </span>
            </div>
          </div>

          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {animated && (
              <div
                className="rm-shimmer absolute inset-y-0 w-[40%]"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.28) 50%, transparent 100%)",
                  animationDelay: "2.4s",
                }}
              />
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
